import { Location } from '@angular/common';
import { Component, NgZone, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import PocketBase from 'pocketbase';
import { Subscription } from 'rxjs';
import { IRequestPayment, IResponsePocketBasePayment } from 'src/app/shared/interfaces/interface';
import { CommonService } from 'src/app/services/common/common.service';
import { DataPocketService } from 'src/app/services/data-pocket/data-pocket.service';
import { DataService } from 'src/app/services/data/data.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { AppRoute } from 'src/app/shared/constants/app-route.constant';
import {
  InterfaceErrorText,
  InterfaceText,
  OmiseStatusKey,
  OmiseStatusName,
  PaymentChannel,
  PaymentCurrency,
  PaymentDescription,
  PaymentType,
  PocketbaseAction,
  StorageKey,
} from 'src/app/shared/constants/global-config.constant';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';

/** How long after the QR appears before the realtime subscription is set up. */
const SUBSCRIBE_DELAY_MS = 3000;

/** How long to wait for the Omise webhook to write the Pocketbase record before looking. */
const POCKETBASE_TRX_WAIT_MS = 3000;

/**
 * How often the background check asks the API whether the payment landed. This is the
 * backstop for the realtime subscription — see startPaymentPolling().
 */
const PAYMENT_POLL_INTERVAL_MS = 10000;

@Component({
  selector: 'app-payment-alipay',
  templateUrl: './payment-alipay.component.html',
  styleUrls: ['./payment-alipay.component.scss']
})
export class PaymentAlipayComponent implements OnDestroy {

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private dataService: DataService,
    private dataPocketService: DataPocketService,
    private themeService: ThemeService,
    public commonService: CommonService,
    private ngZone: NgZone,
  ) { }

  TXT_PAYMENT_SCAN_SCULPTURE = InterfaceText.PAYMENT_SCAN_SCULPTURE;
  TXT_PAYMENT_ALIPAY_TITLE = InterfaceText.PAYMENT_ALIPAY_TITLE
  TXT_PAYMENT_ALIPAY_SUBTITLE = InterfaceText.PAYMENT_ALIPAY_SUBTITLE
  TXT_PAYMENT_SCAN_QR_ERROR_TITLE = InterfaceErrorText.PAYMENT_SCAN_QR_ERROR_TITLE;
  TXT_PAYMENT_SCAN_QR_ERROR_SUBTITLE = InterfaceErrorText.PAYMENT_SCAN_QR_ERROR_SUBTITLE;
  TXT_PAYMENT_SCAN_CURRENCY = InterfaceText.PAYMENT_SCAN_CURRENCY

  theme = this.themeService.getTheme();

  promoType: string = null;
  totalPrice: number = 0;
  vatPrice: number = 0;
  discountPrice: number = 0;
  deductedPrice: number = 0;

  amountSum: number = 0;

  PocketbaseTrxId: string = null;
  SculptureTrxId: string = null;
  SculpturePaymentId: string = null;
  OmiseChargeId: string = null;
  OmiseAlipayUrl: string = null;

  isQrError: boolean = false;

  /** Guards against the realtime callback and the background poll both completing the payment. */
  private isPaymentHandled: boolean = false;
  private pollTimer: any = null;
  private subscribeTimer: any = null;
  private queryParamsSub: Subscription;

  ngOnDestroy(): void {
    this.stopPaymentPolling();

    if (this.subscribeTimer !== null) {
      clearTimeout(this.subscribeTimer);
      this.subscribeTimer = null;
    }

    this.unsubscribeRealtime();

    if (this.queryParamsSub) {
      this.queryParamsSub.unsubscribe();
    }
  }

  async ngOnInit() {
    this.queryParamsSub = this.activatedRoute.queryParams.subscribe(params => {
      this.promoType = params.promo_type;
      this.totalPrice = params.total_price;
      this.vatPrice = params.vat_price;
      this.discountPrice = params.discount_price;
      this.deductedPrice = params.deducted_price;

      this.getAlipayQR();

    }
    );
    this.amountSum = this.totalPrice;
  }

  async getAlipayQR() {
    try {
      this.commonService.isLoading = true;
      let payload: IRequestPayment = {
        branch_code: localStorage.getItem(StorageKey.BRANCH_CODE),
        payment_type: PaymentType.ALIPAY,
        channel: PaymentChannel.OMISE,
        // Guard at the boundary as well as at source: these arrive as query params,
        // and the API rejects anything with more than two decimals.
        amount_net: this.commonService.roundNumberToTwoDecimalPlaces(Number(this.deductedPrice)),
        amount_vat: this.commonService.roundNumberToTwoDecimalPlaces(Number(this.vatPrice)),
        description: PaymentDescription.DEFAULT,
        amount_discount: this.commonService.roundNumberToTwoDecimalPlaces(Number(this.discountPrice)),
        currency: PaymentCurrency.THB,
      }
      const result = await this.dataService.getCreatePayment(payload).toPromise();

      this.SculptureTrxId = result.trx_id;
      this.SculpturePaymentId = result.id.toString();
      this.OmiseChargeId = result.ref_id_2;
      this.OmiseAlipayUrl = result.ref_id_3;
      console.log(this.OmiseAlipayUrl);
      if (this.OmiseAlipayUrl === null) {
        this.isQrError = true;
      }
      else {
        this.subscribeTimer = setTimeout(() => {
          this.subscribeTimer = null;
          this.subscribeToCheckPaid();
        }, SUBSCRIBE_DELAY_MS);

        // Started here rather than only on failure: by the time we know the subscription is
        // dead it is too late to notice a payment that already happened.
        this.startPaymentPolling();
      }

    }
    catch (err) {
      this.isQrError = true;
      console.log(err);
    }
    finally {
      this.commonService.isLoading = false;
    }

  }

  pbClient = new PocketBase(environment.pocket_endpoint);

  /**
   * Sets up the realtime subscription — the fast path for noticing the payment.
   *
   * Every failure here is caught and swallowed on purpose. This used to have no try/catch
   * and was fired from a bare setTimeout, so any rejection became an unhandled promise:
   * the QR stayed on screen looking healthy while nothing was listening any more. Now
   * failure just means the background poll started in getAlipayQR() is the only listener.
   */
  async subscribeToCheckPaid() {
    try {
      await this.dataPocketService.loginPocketbase();
      await this.commonService.sleep(POCKETBASE_TRX_WAIT_MS);

      const trx = await this.dataPocketService.getTransactionIdByChargeId(this.OmiseChargeId).toPromise();

      if (trx.items.length > 0) {
        this.PocketbaseTrxId = trx.items[0].id;

        await this.pbClient.collection('co_app_users').authWithPassword(
          environment.pocket_acc,
          localStorage.getItem(StorageKey.POCKET_KEY)
        );

        console.log('pocketbase subscribing to transaction: ALIPAY');
        // Inside the zone on purpose. The Pocketbase SDK reconnects its EventSource from
        // `eventSource.onerror`, which zone.js does not patch, so after the first drop (a few
        // idle minutes on this page) every event arrives in the root zone. Without this the
        // paid callback navigated to /payment-success with no change detection behind it:
        // the URL changed and the page stayed white. See the spec for the booth history.
        this.pbClient.realtime.subscribe(`${environment.pocket_group}/${this.PocketbaseTrxId}`, (data) => this.ngZone.run(async () => {
          if (data.action === PocketbaseAction.UPDATE) {
            if (data.record.key === OmiseStatusKey.COMPLETE) {
              await this.handlePaymentPaid();
            }
          }
        }));
      }
      else {
        // The Omise webhook has not written the record within the fixed wait above. Nothing
        // retries that lookup, so the background poll is what notices the payment now.
        console.log('pocketbase trx not found ~ relying on payment polling');
      }
    }
    catch (err) {
      console.log('subscribeToCheckPaid failed ~ relying on payment polling', err);
    }
  }

  /**
   * Background check that the payment landed, and the reason a dead subscription is no
   * longer fatal. Runs alongside the realtime subscription rather than only on failure,
   * because a subscription can also die silently *after* it was established.
   */
  private startPaymentPolling() {
    if (this.pollTimer !== null || this.isPaymentHandled || this.SculptureTrxId === null) {
      return;
    }

    console.log('payment polling started every', PAYMENT_POLL_INTERVAL_MS, 'ms');
    this.pollTimer = setInterval(() => this.pollPaymentOnce(), PAYMENT_POLL_INTERVAL_MS);
  }

  private async pollPaymentOnce() {
    if (this.isPaymentHandled) {
      return;
    }

    try {
      // Deliberately not checkPaymentIsPaid(): that raises commonService.isLoading, whose
      // overlay covers the buttons, and shows the "not paid yet" popup on every miss. A
      // background check has to be invisible until it actually finds something. The silent
      // flag also suppresses the retry toast, which would close an open dialog.
      const payment = await this.dataService.getPaymentInfo(this.SculptureTrxId, true).toPromise();

      if (payment && payment.is_paid) {
        console.log('payment polling found the payment ~ realtime did not deliver it');
        await this.handlePaymentPaid();
      }
    }
    catch (err) {
      // Stay quiet and try again on the next tick.
    }
  }

  private stopPaymentPolling() {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /**
   * The single place a confirmed payment is acted on, so the realtime callback, the poll
   * and the CONFIRM button cannot navigate to the success page more than once between them.
   */
  private async handlePaymentPaid() {
    if (this.isPaymentHandled) {
      return;
    }
    this.isPaymentHandled = true;

    this.stopPaymentPolling();
    await this.updatePaymentDone();
    this.unsubscribeRealtime();
    this.goToSuccess();
  }

  /** No-ops when no subscription was ever established, rather than unsubscribing '.../null'. */
  private unsubscribeRealtime() {
    if (this.PocketbaseTrxId === null) {
      return;
    }

    try {
      this.pbClient.realtime.unsubscribe(`${environment.pocket_group}/${this.PocketbaseTrxId}`);
    }
    catch (err) {
      console.log('realtime unsubscribe failed', err);
    }
  }

  async updatePaymentDone() {
    this.commonService.isLoading = true;
    try {

      this.commonService.setPaymentTrxId(this.SculptureTrxId);

      // On its own: the server may already have marked it paid (400 "already paid"), and
      // that must not skip marking the promo used.
      try {
        let payload1 = { is_paid: true }
        await this.dataService.updatePaymentByTrxId(this.SculptureTrxId, payload1).toPromise();
      }
      catch (err) {
        console.log('payment already updated, just to make sure', JSON.stringify(err));
      }
      if (localStorage.getItem(StorageKey.PROMO_ID) !== null) {
        let payload3: any = { is_used: true, payment_id: this.SculpturePaymentId }
        await this.dataService.updatePromotionById(localStorage.getItem(StorageKey.PROMO_ID), payload3).toPromise();
      }

    }
    catch (err) {
      console.log(JSON.stringify(err));
      if (err.status === 400) {
        console.log('payment already updated, just to make sure');
      }
    }
    finally {
      this.commonService.isLoading = false;
    }
  }

  goToSuccess() {
    this.router.navigate([AppRoute.PAYMENT_SUCCESS]);
  }

  onBack() {
    if (!this.isQrError) {
      this.showBackWarningPopup();
    }
    else {
      this.unsubscribeRealtime();
      this.location.back();
    }

  }

  showBackWarningPopup() {
    Swal.fire({
      icon: 'warning',
      title: InterfaceText.PAYMENT_SCAN_PAYMENT_CANCEL_TITLE_EN,
      html: InterfaceText.PAYMENT_SCAN_PAYMENT_CANCEL_EN,
      showDenyButton: true,
      confirmButtonText: InterfaceText.PAYMENT_SCAN_PAYMENT_CANCEL_STAY_EN,
      denyButtonText: InterfaceText.PAYMENT_SCAN_PAYMENT_CANCEL_BACK_EN,
    }).then((result) => {
      if (result.isConfirmed) {
        //do nothing because user want to stay
      } else if (result.isDenied) {
        this.unsubscribeRealtime();
        this.location.back();
      }
    })
  }

  onCheck() {
    console.log("checking");
    this.checkPaymentIsPaid();
  }

  async showUnknowErrorPopup() {
    const isConfirmed = await this.commonService.showUnknowErrorPopupAndConfirm();
    if (!isConfirmed) {
      console.log('unsubscribing');
      this.unsubscribeRealtime();
      this.location.back();
    }
  }

  checkLabel: string = InterfaceText.PAYMENT_SCAN_CONFIRM_BUTTON;
  async checkQR() {
    try {
      this.commonService.isLoading = true;
      this.checkLabel = InterfaceText.PAYMENT_SCAN_CONFIRM_LOADING;

      const pocketBaseResult: IResponsePocketBasePayment = await this.dataPocketService.getPaymentByTrxId(this.PocketbaseTrxId).toPromise();
      if (pocketBaseResult.items.length > 0) {
        if (pocketBaseResult.items[0].status === OmiseStatusName.SUCCESS) {
          await this.updatePaymentDone();
          this.pbClient.realtime.unsubscribe(`${environment.pocket_group}/${this.PocketbaseTrxId}`);
          this.goToSuccess();
        }
        else {
          this.commonService.showPaymentNotPaidPopup();
        }
      }
    }
    catch (err) {
      this.commonService.isLoading = false;
      this.checkLabel = InterfaceText.PAYMENT_SCAN_CONFIRM_BUTTON;
      console.log(JSON.stringify(err))
    }
    finally {
      this.commonService.isLoading = false;
      this.checkLabel = InterfaceText.PAYMENT_SCAN_CONFIRM_BUTTON;
    }
  }

  /** True while a CONFIRM check is in flight; extra taps are ignored and the button is disabled. */
  isChecking: boolean = false;

  /**
   * CONFIRM: asks the API to check with Omise directly rather than waiting for the webhook to
   * reach the DB. One request per tap, no retries — a failed check says so and the customer
   * taps again; the background poll and realtime subscription keep running meanwhile.
   */
  async checkPaymentIsPaid() {
    if (this.isChecking || this.isPaymentHandled || this.SculptureTrxId === null) {
      return;
    }

    this.isChecking = true;
    this.commonService.isLoading = true;
    this.checkLabel = InterfaceText.PAYMENT_SCAN_CONFIRM_LOADING;
    try {
      const result = await this.dataService.verifyPayment(this.SculptureTrxId).toPromise();
      if (result.status === 'paid') {
        await this.handlePaymentPaid();
      }
      else if (result.status === 'failed') {
        this.stopPaymentPolling();
        await this.commonService.showPaymentFailedPopup();
        // Same exit as this page's Back button.
        this.unsubscribeRealtime();
        this.location.back();
      }
      else {
        this.commonService.showPaymentNotPaidPopup();
      }
    }
    catch (err) {
      console.log('verify payment failed', JSON.stringify(err));
      this.commonService.showPaymentCheckErrorPopup();
    }
    finally {
      this.isChecking = false;
      this.commonService.isLoading = false;
      this.checkLabel = InterfaceText.PAYMENT_SCAN_CONFIRM_BUTTON;
    }
  }


}
